import React, {useCallback, useMemo} from 'react';
import {Drawer} from 'components/customer/Product/entity/Drawer';
import {useFormikContext} from 'formik';
import {FaceHeightField} from 'components/customer/Product/Drawer/FaceHeightField';

interface DrawerFaceHeightProps {
    runnerIndex: number;
    fieldName: string;
    drawerStyleFieldName: string;
    // typing a height fixes the drawer, for layouts with no style dropdown
    setsFixedOnChange?: boolean;
    // sits inside the control column so the row still lines up
    trailing?: React.ReactNode;
}

export const DrawerFaceHeight = ({
    runnerIndex,
    fieldName,
    drawerStyleFieldName,
    setsFixedOnChange = false,
    trailing = null,
}: DrawerFaceHeightProps) => {
    const {values, setFieldValue, setTouched} = useFormikContext<{
        drawers: Drawer[];
    }>();

    const {enabled, value} = useMemo(() => {
        let isEnabled = false;
        let value = null;
        if (values?.drawers?.length) {
            const drawer = values.drawers[Number(runnerIndex)];
            const isAuto = drawer[drawerStyleFieldName as keyof Drawer] == 1;

            if (setsFixedOnChange) {
                // one drawer has to stay on auto to take the leftover space
                const autoCount = values.drawers.filter(
                    (item) => item[drawerStyleFieldName as keyof Drawer] == 1
                ).length;

                isEnabled = !(isAuto && autoCount <= 1);
            } else {
                isEnabled = !isAuto;
            }
            value = drawer[fieldName as keyof Drawer];
        }
        return {
            enabled: isEnabled,
            value,
        };
    }, [
        values?.drawers,
        drawerStyleFieldName,
        fieldName,
        runnerIndex,
        setsFixedOnChange,
    ]);

    const commitHandler = useCallback(
        (index: number, height: number) => {
            void setTouched({drawers: true});

            if (setsFixedOnChange) {
                void setFieldValue(
                    `drawers[${index}].${drawerStyleFieldName}`,
                    0
                );
            }

            void setFieldValue(`drawers[${index}].${fieldName}`, height);
        },
        [
            drawerStyleFieldName,
            fieldName,
            setFieldValue,
            setTouched,
            setsFixedOnChange,
        ]
    );

    return (
        <FaceHeightField
            index={runnerIndex}
            fieldName={fieldName}
            value={value as string | number}
            disabled={!enabled}
            sharesDraft={setsFixedOnChange}
            trailing={trailing}
            onCommit={commitHandler}
        />
    );
};
